import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DateField from '../components/DateField.jsx';
import QuickPeriod from '../components/QuickPeriod.jsx';
import { api, withQuery, money, quantity, date, dateTime, isoDate } from '../api.js';

/**
 * Услуги — процесът от приемане до продажба.
 *
 * Ремонтите и зарежданията се приемат и изписват в Деливери (техник,
 * оплакване, извършено); тук към всяка се вижда продажбата ѝ в Тефтер —
 * вложените части, себестойността и печалбата. API: /api/services.
 */

export const ETAP = {
  v_servisa: { label: 'в сервиза', cls: 'badge warn' },
  izpisana: { label: 'изписана, без продажба', cls: 'badge danger' },
  lipsva: { label: 'генерираната продажба я няма', cls: 'badge danger' },
  chernova: { label: 'продажба — чернова', cls: 'badge' },
  prodadena: { label: 'продадена', cls: 'badge ok' },
  stornirana: { label: 'сторнирана', cls: 'badge muted' },
};
const VID = { repair: 'Ремонт', refill: 'Зареждане' };

export default function Services() {
  const [range, setRange] = useState(() => ({
    from: isoDate(new Date(Date.now() - 30 * 86400000)),
    to: isoDate(),
  }));
  const [vid, setVid] = useState('all');
  const [etapF, setEtapF] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [otvoren, setOtvoren] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.get(withQuery('/api/services', { ...range, vid })).then(setData).catch((e) => setError(e.message));
  }, [range, vid]);

  const redove = useMemo(
    () => (data?.redove ?? []).filter((r) => !etapF || r.etap === etapF),
    [data, etapF]);
  const o = data?.obobshtenie;

  return (
    <>
      <div className="page-head">
        <h1>Услуги</h1>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Ремонтите и зарежданията — от приемането в Деливери (техник, оплакване, извършено) до
        продажбата тук (вложени части, себестойност, печалба). Продажба на услуга се генерира от
        самия ремонт или зареждане в Деливери.
      </p>

      <div className="toolbar">
        <label className="field">
          Приети от
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        <div className="field">
          <span aria-hidden="true" style={{ visibility: 'hidden' }}>·</span>
          <QuickPeriod onPick={({ from, to }) => setRange({ from, to })} />
        </div>
        {[['all', 'Всички'], ['repair', 'Ремонти'], ['refill', 'Зареждания']].map(([k, l]) => (
          <button key={k} type="button" className={vid === k ? 'primary' : ''} onClick={() => setVid(k)}>{l}</button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}
      {!data && !error && <div className="card"><div className="empty">Зареждане…</div></div>}

      {o && (
        <>
          <div className="toolbar">
            <span className="badge">{o.uslugi} услуги</span>
            <span className="badge">приход {money(o.prihod)}</span>
            <span className="badge">вложено {money(o.sebestoinost)}</span>
            <span className="badge ok">печалба {money(o.pechalba)}</span>
            {o.sredniDni !== null && <span className="badge">средно {o.sredniDni} дни до изписване</span>}
          </div>
          <div className="toolbar">
            <button type="button" className={etapF === '' ? 'primary' : ''} onClick={() => setEtapF('')}>Всички етапи</button>
            {Object.entries(ETAP).map(([k, e]) => (
              <button key={k} type="button" className={etapF === k ? 'primary' : ''} onClick={() => setEtapF(k)}>
                {e.label}{o.poEtap[k] ? ` (${o.poEtap[k]})` : ''}
              </button>
            ))}
          </div>

          <div className="card">
            <div className="card-head">По техник</div>
            <table className="compact">
              <thead>
                <tr><th>Техник</th><th className="num">Услуги</th><th className="num">Приход</th><th className="num">Печалба</th></tr>
              </thead>
              <tbody>
                {o.poTehnik.map((t) => (
                  <tr key={t.ime}>
                    <td>{t.ime}</td>
                    <td className="num">{t.uslugi}</td>
                    <td className="num">{money(t.prihod)}</td>
                    <td className="num">{money(t.pechalba)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <table className="compact">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Приета</th>
                  <th>Услуга</th>
                  <th>Клиент</th>
                  <th>Техник</th>
                  <th>Етап</th>
                  <th style={{ width: 90 }}>Изписана</th>
                  <th>Продажба</th>
                  <th className="num">Приход</th>
                  <th className="num">Вложено</th>
                  <th className="num">Печалба</th>
                </tr>
              </thead>
              <tbody>
                {redove.map((r) => {
                  const k = `${r.vid}-${r.id}`;
                  const p = r.prodazhba;
                  return (
                    <Fragment key={k}>
                      <tr onClick={() => setOtvoren(otvoren === k ? null : k)} style={{ cursor: 'pointer' }}>
                        <td>{date(r.priet)}</td>
                        <td>
                          {VID[r.vid]}{r.nomer ? ` #${r.nomer}` : ''} · {r.predmet}
                          <div className="muted" style={{ fontSize: 11 }}>№ {r.serien}</div>
                        </td>
                        <td>{r.klient ?? '—'}</td>
                        <td>{r.tehnik ?? '—'}</td>
                        <td><span className={ETAP[r.etap]?.cls}>{ETAP[r.etap]?.label}</span></td>
                        <td>{r.izpisan ? date(r.izpisan) : ''}</td>
                        <td>
                          {p ? <Link to={`/documents/${p.docId}`} onClick={(e) => e.stopPropagation()}>{p.docNumber}</Link> : ''}
                          {p && p.obshtaZa > 1 && <div className="muted" style={{ fontSize: 11 }}>обща за {p.obshtaZa}</div>}
                        </td>
                        <td className="num">{p?.prihod ? money(p.prihod) : ''}</td>
                        <td className="num">{p?.sebestoinost ? money(p.sebestoinost) : ''}</td>
                        <td className="num">{p?.prihod ? money(p.prihod - p.sebestoinost) : ''}</td>
                      </tr>
                      {otvoren === k && (
                        <tr>
                          <td />
                          <td colSpan={9}>
                            {r.oplakvane && <div><strong>Оплакване:</strong> {r.oplakvane}</div>}
                            {r.izvarsheno && <div><strong>Извършено:</strong> {r.izvarsheno}</div>}
                            <div className="muted">
                              Приета {dateTime(r.priet)}{r.izpisan ? ` · изписана ${dateTime(r.izpisan)}` : ''}
                              {r.dni !== null ? ` · ${r.dni} дни` : ''}
                              {r.cenaVDeliveri ? ` · цена в Деливери: ${r.cenaVDeliveri}` : ''}
                            </div>
                            {p && (
                              <table className="compact" style={{ marginTop: 6 }}>
                                <thead>
                                  <tr><th>Ред в продажбата</th><th className="num">Кол.</th><th className="num">Приход</th><th className="num">Себестойност</th></tr>
                                </thead>
                                <tbody>
                                  {p.redove.map((x, i) => (
                                    <tr key={i}>
                                      <td>{x.opisanie}{x.vlozheno && <span className="badge muted" style={{ marginLeft: 6 }}>вложено</span>}</td>
                                      <td className="num">{quantity(x.kolichestvo)} {x.mqrka}</td>
                                      <td className="num">{money(x.prihod)}</td>
                                      <td className="num">{money(x.sebestoinost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {!p && r.izpisan && (
                              <div className="notice" style={{ marginTop: 6 }}>
                                Изписана без продажба — генерира се от ремонта/зареждането в Деливери.
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {redove.length === 0 && <tr><td colSpan={10} className="empty">Няма услуги за периода</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/** В продажбата: от коя услуга е дошла. */
export function DocumentServiceOrigin({ documentId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/api/documents/${documentId}/service-origin`).then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [documentId]);
  if (!items || items.length === 0) return null;
  return (
    <div className="notice">
      <strong>От услуга:</strong>{' '}
      {items.map((u, i) => (
        <span key={`${u.vid}-${u.id}`}>
          {i > 0 && '; '}
          {VID[u.vid]}{u.nomer ? ` #${u.nomer}` : ''} {u.predmet} (№ {u.serien})
          {u.tehnik ? `, техник ${u.tehnik}` : ''}
          {`, приета ${date(u.priet)}`}{u.izpisan ? `, изписана ${date(u.izpisan)}` : ', още в сервиза'}
          {u.izvarsheno ? ` — ${u.izvarsheno}` : ''}
        </span>
      ))}
    </div>
  );
}
